import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
} from "@reducate/ui";
import { X } from "lucide-react";
import { useRef } from "react";
import type { ReducateModalProps } from "../types/reducate.js";

const widths = { default: "sm:max-w-lg", medium: "sm:max-w-2xl", wide: "sm:max-w-5xl" };

export function Modal({
  open,
  title,
  description,
  controlId,
  closeLabel,
  children,
  onClose,
  size = "default",
}: ReducateModalProps) {
  const previousFocus = useRef<HTMLElement | null>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        aria-labelledby={`${controlId}-title`}
        aria-describedby={description ? `${controlId}-description` : undefined}
        className={`max-h-[90dvh] overflow-y-auto ${widths[size]}`}
        data-control-id={controlId}
        onOpenAutoFocus={(event) => {
          previousFocus.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
          event.preventDefault();
          closeButton.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (previousFocus.current?.isConnected) previousFocus.current.focus();
        }}
        showCloseButton={false}
      >
        <DialogHeader className="pr-10">
          <DialogTitle id={`${controlId}-title`}>{title}</DialogTitle>
          {description ? (
            <DialogDescription id={`${controlId}-description`}>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogClose asChild>
          <Button
            aria-label={closeLabel}
            className="absolute top-2 right-2"
            data-control-id={`${controlId}.close`}
            ref={closeButton}
            size="icon"
            variant="ghost"
            type="button"
          >
            <X aria-hidden="true" />
          </Button>
        </DialogClose>
        {children}
      </DialogContent>
    </Dialog>
  );
}
